import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from '@lwt-helix/layout';
import Chart from 'react-apexcharts';
import { AgentMovementIndicator } from './AgentMovementIndicator';
import { useSearchStore } from '../../store/search/store';
import { getDashboardIndicators } from '../helpers/dataFormatters';
import { indicatorsSettingsByKeyName, terms } from './constants/index';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { searchRoutes as sr } from '../../constants';
import { getSearchDescriptionTitle } from './helpers/breakdowns';
import { getPDFCustomChartProps } from '../../common/helpers/export';
import { usePDFStore } from '../../store/pdf/store';
import Loader from '@lwt-helix/loader';
import { useSidebarExpanded } from '../../common/hooks/useSidebarExpanded';

const SIDEBAR_EXPANDED_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 68;
const SCROLL_BREAKPOINT = 1200;
const MINIMUM_CHART_WIDTH = 1100;
const CHART_SCROLL_VIEWPORT_WIDTH = 500;
const AVAILABLE_WIDTH_BUFFER = 200;
const COL_HORIZONTAL_PADDING = 30;

export const AgentMovementDashboard = ({ chartProps }) => {
    const history = useHistory();
    const { isSideNavExpanded, isTransitioning } = useSidebarExpanded();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const sidebarWidth = isSideNavExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
    const availableWidth = windowWidth - sidebarWidth + AVAILABLE_WIDTH_BUFFER - COL_HORIZONTAL_PADDING;
    const needsScroll = availableWidth < SCROLL_BREAKPOINT;

    const getAdjustedChartProps = (baseProps, needsScroll) => {
        const adjustedProps = { ...baseProps };
        adjustedProps.width = needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%';
        return adjustedProps;
    };

    const adjustedChartProps = getAdjustedChartProps(chartProps, needsScroll);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize, true);
        return () => window.removeEventListener('resize', handleResize, true);
    }, []);

    const chartPropsPDF = getPDFCustomChartProps({
        chartProps,
        dataLabelsFontSize: '22px',
        dataLabelsOffsetY: -20,
        gridPadding: { top: 0, bottom: 60 },
        height: 700,
        xAxisLabelsFontSize: '19px',
    });

    const {
        indicators,
        selectedOfficeListSearch,
    } = useSearchStore(state => {
        const moduleState = state[state.selectedModule];
        return ({
            indicators: getDashboardIndicators(moduleState?.totals, indicatorsSettingsByKeyName),
            selectedOfficeListSearch: moduleState.search.selectedOfficeListSearch,
        });
    });

    const {
        isPDFLoading,
    } = usePDFStore((state) => ({
        isPDFLoading: state.downloadModalStatus.isPDFLoading,
    }));

    const handleOfficeListBreakdownRedirect = () => history.push(sr.officeListBreakdown);
    const searchDescriptionTitle = getSearchDescriptionTitle(indicators, selectedOfficeListSearch);

    const renderIndicators = () => (
        <div className='indicators-container' style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'flex-end' }}>
            {indicators.map((total) => (
                <div key={total.key} style={{ flex: '0 0 250px', minWidth: '250px' }}>
                    <AgentMovementIndicator
                        label={total.label}
                        value={total.value}
                        tooltip={total.tooltip}
                        isCurrency={total.isCurrency}
                    />
                </div>
            ))}
        </div>
    );

    const chartIndicators = renderIndicators();

    return (
        <>
            <div className='agent-movement-dashboard raised-border'>
                <Row>
                    <Col xs={12} md={5} lg={4} className='d-flex justify-content-center'>
                        <div className='gained-agents'>
                            <h5 className='helix-display-x-small mb-0'>{searchDescriptionTitle}</h5>
                            <p onClick={handleOfficeListBreakdownRedirect} className="view-breakdown clickable mb-0">{terms.viewBreakdown}</p>
                        </div>
                    </Col>
                    <Col xs={12} md={7} lg={8} className='d-flex justify-content-center justify-content-lg-end'>
                        {chartIndicators}
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <div
                            id="agent-movement-chart-graph"
                            className={`raised-border mb-3 bg-white ${needsScroll ? 'needs-scroll' : 'no-scroll'}`}
                        >
                            {!isTransitioning ? (
                                <div
                                    className={`chart-wrapper ${needsScroll ? 'needs-scroll' : ''}`}
                                    style={needsScroll ? { width: `${CHART_SCROLL_VIEWPORT_WIDTH}px` } : {}}
                                >
                                    <Chart
                                        {...adjustedChartProps}
                                        width={needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%'}
                                        options={{
                                            ...adjustedChartProps.options,
                                            chart: {
                                                ...adjustedChartProps.options.chart,
                                                width: needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%'
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <div style={{ minHeight: `${chartProps?.height || 390}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Loader />
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            {isPDFLoading && (
                <div className='pdf-chart-agent-movement chart-capture-area'>
                    <Row>
                        <Col sm={12} md={5} lg={4} className='d-flex justify-content-left'>
                            <div>
                                <h5 className='helix-display-x-small mb-0'>{searchDescriptionTitle}</h5>
                            </div>
                        </Col>
                        <Col sm={12} md={7} lg={8} className='d-flex justify-content-center justify-content-lg-end'>
                            {chartIndicators}
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <Chart {...chartPropsPDF} />
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );
};

AgentMovementDashboard.propTypes = {
    chartProps: PropTypes.object,
};

export default AgentMovementDashboard;
